import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  FileText,
  CheckCircle,
  Star,
  TrendingUp,
  Settings,
  Plus,
  Search,
  Eye,
  Pencil,
  Copy,
  Trash2,
  ChevronDown,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { cn } from "../../components/ui/utils";
import api from "../../utils/api";
import { toast } from "sonner";
import { useData } from "../../context/DataContext";
import { MultiSelect } from "../../components/ui/multi-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

interface InterviewTemplate {
  id: number;
  name: string;
  isDefault: boolean;
  description: string | null;
  type: string;
  level: string;
  isActive: boolean;
  jobRoles: { id: number; title: string }[];
  departments: { id: number; departmentName: string }[];
  targetValues: { id: number; name: string }[];
  questionSets: { name: string; description: string; durationMinutes: number; questionIds: number[] }[];
  createdAt: string;
  usage?: number;
  rating?: number;
  successRate?: number;
  defaultForJobRoleIds?: number[];
}

const TYPE_LABELS: Record<string, string> = {
  VALUE_BASED: "Values-Based",
  TECHNICAL: "Technical",
  BEHAVIORAL: "Behavioral",
  LEADERSHIP: "Leadership",
  MIXED: "Mixed",
};

const LEVEL_LABELS: Record<string, string> = {
  ALL: "All Levels",
  ENTRY: "Entry Level",
  MID: "Mid Level",
  SENIOR: "Senior Level",
  EXECUTIVE: "Executive",
};

const InterviewTemplates: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  if (!ability.can(ACTIONS.READ, RESOURCES.INTERVIEW_TEMPLATE)) {
    return <AccessRestricted />;
  }
  const [activeTab, setActiveTab] = useState("templates");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All Templates");

  const { jobRoles } = useData();

  const [templates, setTemplates] = useState<InterviewTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<InterviewTemplate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Set as default dialog state
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  const [editingDefaultTemplate, setEditingDefaultTemplate] = useState<InterviewTemplate | null>(null);
  const [selectedDefaultJobRoleIds, setSelectedDefaultJobRoleIds] = useState<string[]>([]);
  const [defaultSubmitting, setDefaultSubmitting] = useState(false);

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState<InterviewTemplate | null>(null);

  // Clone loading
  const [cloneLoadingId, setCloneLoadingId] = useState<number | null>(null);

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await api.get<{ success: boolean; templates: InterviewTemplate[] }>("/api/interview-templates");
      if (res.success) setTemplates(res.templates);
    } catch (err) {
      console.error("Failed to load interview templates:", err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async () => {
    if (!templateToDelete) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/interview-templates/${templateToDelete.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== templateToDelete.id));
      toast.success("Template deleted");
      setTemplateToDelete(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to delete template");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openSetDefaultDialog = (template: InterviewTemplate) => {
    setEditingDefaultTemplate(template);
    setSelectedDefaultJobRoleIds((template.defaultForJobRoleIds || []).map(String));
    setDefaultDialogOpen(true);
  };

  const handleSetDefault = async () => {
    if (!editingDefaultTemplate) return;
    setDefaultSubmitting(true);
    try {
      const res = await api.put<{ success: boolean; template: InterviewTemplate }>(
        `/api/interview-templates/${editingDefaultTemplate.id}`,
        {
          defaultForJobRoleIds: selectedDefaultJobRoleIds.map(Number),
        }
      );
      if (res.success) {
        toast.success("Default job roles updated");
        setTemplates((prev) =>
          prev.map((t) =>
            t.id === editingDefaultTemplate.id
              ? { ...t, defaultForJobRoleIds: selectedDefaultJobRoleIds.map(Number) }
              : t
          )
        );
        setDefaultDialogOpen(false);
        setEditingDefaultTemplate(null);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update default job roles");
    } finally {
      setDefaultSubmitting(false);
    }
  };

  const openViewDialog = (template: InterviewTemplate) => {
    setViewingTemplate(template);
    setViewDialogOpen(true);
  };

  const handleClone = async (template: InterviewTemplate) => {
    setCloneLoadingId(template.id);
    try {
      const res = await api.post<{ success: boolean; template: InterviewTemplate }>("/api/interview-templates", {
        name: `${template.name} (Copy)`,
        type: template.type,
        level: template.level,
        description: template.description,
        jobRoleIds: (template.jobRoles || []).map((jr) => jr.id),
        departmentIds: (template.departments || []).map((d) => d.id),
        targetValueIds: (template.targetValues || []).map((tv) => tv.id),
        questionSets: template.questionSets || [],
        scoringConfig: {},
      });
      if (res.success) {
        toast.success("Template cloned successfully");
        setTemplates((prev) => [...prev, res.template]);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to clone template");
    } finally {
      setCloneLoadingId(null);
    }
  };

  const stats = useMemo(() => {
    const total = templates.length;
    const active = templates.filter((t) => t.isActive).length;
    const defaultCount = templates.filter((t) => t.isDefault || (t.defaultForJobRoleIds && t.defaultForJobRoleIds.length > 0)).length;
    return { total, active, default: defaultCount };
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesSearch =
        searchQuery === "" ||
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        t.jobRoles.some((jr) => jr.title.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesType =
        typeFilter === "All Templates" || TYPE_LABELS[t.type] === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [templates, searchQuery, typeFilter]);

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate("/recruitment")}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Recruitment</span>
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileText size={28} className="text-gray-900" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Interview Templates
            </h1>
            <p className="text-gray-500 text-sm font-medium">
              Manage interview templates and question sets
            </p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <Button
            variant="outline"
            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 h-10 rounded-xl text-sm font-bold"
            onClick={() => {
              /* TODO: default settings */
            }}
          >
            <Settings size={16} />
            <span>Default Settings</span>
          </Button>
          <Button
            className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold"
            onClick={() => navigate("/recruitment/interview-templates/new")}
          >
            <Plus size={16} />
            <span>New Template</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
          <div className="flex justify-between items-start">
            <div className="z-10">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                Total Templates
              </p>
              <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">
                {stats.total}
              </h3>
            </div>
            <div className="p-2.5 bg-blue-50 rounded-xl">
              <FileText className="text-blue-500" size={22} />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
          <div className="flex justify-between items-start">
            <div className="z-10">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                Active Templates
              </p>
              <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">
                {stats.active}
              </h3>
            </div>
            <div className="p-2.5 bg-green-50 rounded-xl">
              <CheckCircle className="text-green-500" size={22} />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
          <div className="flex justify-between items-start">
            <div className="z-10">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                Default Templates
              </p>
              <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">
                {stats.default}
              </h3>
            </div>
            <div className="p-2.5 bg-amber-50 rounded-xl">
              <Star className="text-amber-500" size={22} />
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
          <div className="flex justify-between items-start">
            <div className="z-10">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                Total Questions
              </p>
              <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">
                {templates.reduce((sum, t) => sum + (t.questionSets?.reduce((s, qs) => s + (qs.questionIds?.length || 0), 0) || 0), 0)}
              </h3>
            </div>
            <div className="p-2.5 bg-purple-50 rounded-xl">
              <TrendingUp className="text-purple-500" size={22} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-2">
          <TabsTrigger
            value="templates"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Templates ({stats.total})
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Analytics
          </TabsTrigger>
          <TabsTrigger
            value="settings"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-0 space-y-6">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="relative w-full">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search templates..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-100 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-11 rounded-xl text-sm font-bold flex items-center justify-between gap-2 px-4 shrink-0"
                >
                  <span>{typeFilter}</span>
                  <ChevronDown size={14} className="text-gray-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px] p-2 rounded-xl">
                {["All Templates", "Values-Based", "Technical", "Behavioural"].map(
                  (type) => (
                    <DropdownMenuItem
                      key={type}
                      className={cn(
                        "rounded-lg text-sm font-bold cursor-pointer py-2.5",
                        typeFilter === type
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-600"
                      )}
                      onClick={() => setTypeFilter(type)}
                    >
                      {type}
                    </DropdownMenuItem>
                  )
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Loading State */}
          {templatesLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-gray-400" />
              <span className="ml-3 text-sm text-gray-500 font-medium">Loading templates...</span>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-16">
              <FileText size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm font-bold text-gray-900">No templates found</p>
              <p className="text-xs text-gray-500 font-medium mt-1">
                {templates.length === 0
                  ? "Create your first interview template to get started."
                  : "Try adjusting your search or filters."}
              </p>
              {templates.length === 0 && (
                <Button
                  className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-10 rounded-xl text-sm font-bold px-4 mt-4"
                  onClick={() => navigate("/recruitment/interview-templates/new")}
                >
                  <Plus size={16} />
                  <span>Create Template</span>
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto no-scrollbar">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">
                        Template
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Level
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Questions
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Duration
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Usage
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Success Rate
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Status
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTemplates.map((template) => {
                      const questionCount = template.questionSets?.reduce((sum, qs) => sum + (qs.questionIds?.length || 0), 0) || 0;
                      const setCount = template.questionSets?.length || 0;
                      const duration = template.questionSets?.reduce((sum, qs) => sum + (qs.durationMinutes || 0), 0) || 0;
                      return (
                        <tr
                          key={template.id}
                          className="hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="px-6 py-5">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-bold text-gray-900">
                                  {template.name}
                                </p>
                                {(template.isDefault || (template.defaultForJobRoleIds && template.defaultForJobRoleIds.length > 0)) && (
                                  <Star size={14} className="text-amber-400 shrink-0" />
                                )}
                              </div>
                              {template.description && (
                                <p className="text-xs text-gray-500 font-medium mt-0.5 max-w-md">
                                  {template.description}
                                </p>
                              )}
                              {template.defaultForJobRoleIds && template.defaultForJobRoleIds.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {template.defaultForJobRoleIds.map((id) => {
                                    const role = jobRoles.find((jr) => String(jr.id) === String(id));
                                    return (
                                      <Badge
                                        key={id}
                                        variant="outline"
                                        className="rounded-lg border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-600"
                                      >
                                        {role?.title || `Role ${id}`}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <Badge
                              variant="outline"
                              className="rounded-full border-0 font-bold px-2.5 py-1 text-[10px] bg-purple-50 text-purple-700"
                            >
                              {TYPE_LABELS[template.type] || template.type}
                            </Badge>
                          </td>
                          <td className="px-6 py-5">
                            <Badge
                              variant="outline"
                              className="rounded-full border-0 font-bold px-2.5 py-1 text-[10px] bg-blue-50 text-blue-700"
                            >
                              {LEVEL_LABELS[template.level] || template.level}
                            </Badge>
                          </td>
                          <td className="px-6 py-5">
                            <div>
                              <p className="text-sm font-bold text-gray-900 text-center">
                                {questionCount}
                              </p>
                              <p className="text-[10px] text-gray-500 font-medium text-center">
                                {setCount} set{setCount !== 1 ? "s" : ""}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <p className="text-sm font-bold text-gray-900 text-center">
                              {duration}m
                            </p>
                          </td>
                          <td className="px-6 py-5">
                            <div>
                              <p className="text-sm font-bold text-gray-900 text-center">
                                {template.usage ?? "—"}
                              </p>
                              <p className="text-[10px] text-gray-500 font-medium text-center">
                                {template.rating ? `Rating: ${template.rating}` : "Rating: —"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <span
                              className={cn(
                                "text-sm font-bold",
                                template.successRate === undefined
                                  ? "text-gray-400"
                                  : template.successRate >= 80
                                  ? "text-green-600"
                                  : template.successRate >= 60
                                  ? "text-yellow-600"
                                  : "text-red-600"
                              )}
                            >
                              {template.successRate !== undefined ? `${template.successRate}%` : "—"}
                            </span>
                          </td>
                          <td className="px-6 py-5">
                            <Badge
                              variant="outline"
                              className={cn(
                                "rounded-full border-0 font-bold px-2.5 py-1 text-[10px]",
                                template.isActive
                                  ? "bg-slate-900 text-white"
                                  : "bg-gray-100 text-gray-500"
                              )}
                            >
                              {template.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="px-6 py-5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => openViewDialog(template)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5"
                                title="View"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => navigate(`/recruitment/interview-templates/${template.id}/edit`)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5"
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={() => handleClone(template)}
                                disabled={cloneLoadingId === template.id}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 disabled:opacity-50"
                                title="Clone"
                              >
                                {cloneLoadingId === template.id ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Copy size={16} />
                                )}
                              </button>
                              <button
                                onClick={() => openSetDefaultDialog(template)}
                                className="text-gray-400 hover:text-amber-500 transition-colors p-1.5"
                                title="Set as default"
                              >
                                <Star size={16} />
                              </button>
                              <button
                                onClick={() => setTemplateToDelete(template)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1.5"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredTemplates.map((template) => {
                  const questionCount = template.questionSets?.reduce((sum, qs) => sum + (qs.questionIds?.length || 0), 0) || 0;
                  const setCount = template.questionSets?.length || 0;
                  const duration = template.questionSets?.reduce((sum, qs) => sum + (qs.durationMinutes || 0), 0) || 0;
                  return (
                    <div
                      key={template.id}
                      className="bg-white border border-gray-100 rounded-2xl p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-gray-900 text-sm">
                              {template.name}
                            </p>
                            {(template.isDefault || (template.defaultForJobRoleIds && template.defaultForJobRoleIds.length > 0)) && (
                              <Star size={14} className="text-amber-400 shrink-0" />
                            )}
                          </div>
                          {template.description && (
                            <p className="text-xs text-gray-500 font-medium mt-0.5">
                              {template.description}
                            </p>
                          )}
                          {template.defaultForJobRoleIds && template.defaultForJobRoleIds.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {template.defaultForJobRoleIds.map((id) => {
                                const role = jobRoles.find((jr) => String(jr.id) === String(id));
                                return (
                                  <Badge
                                    key={id}
                                    variant="outline"
                                    className="rounded-lg border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-600"
                                  >
                                    {role?.title || `Role ${id}`}
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="outline"
                          className="rounded-full border-0 font-bold px-2 py-0.5 text-[10px] bg-purple-50 text-purple-700"
                        >
                          {TYPE_LABELS[template.type] || template.type}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="rounded-full border-0 font-bold px-2 py-0.5 text-[10px] bg-blue-50 text-blue-700"
                        >
                          {LEVEL_LABELS[template.level] || template.level}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={cn(
                            "rounded-full border-0 font-bold px-2 py-0.5 text-[10px]",
                            template.isActive
                              ? "bg-slate-900 text-white"
                              : "bg-gray-100 text-gray-500"
                          )}
                        >
                          {template.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-gray-50 rounded-xl px-3 py-2">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">
                            Questions
                          </span>
                          <span className="font-bold text-gray-700">
                            {questionCount}
                          </span>
                          <span className="text-gray-500 font-medium">
                            {" "}
                            ({setCount})
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-xl px-3 py-2">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">
                            Duration
                          </span>
                          <span className="font-bold text-gray-700">
                            {duration}m
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-xl px-3 py-2">
                          <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">
                            Success
                          </span>
                          <span
                            className={cn(
                              "font-bold",
                              template.successRate === undefined
                                ? "text-gray-400"
                                : template.successRate >= 80
                                ? "text-green-600"
                                : template.successRate >= 60
                                ? "text-yellow-600"
                                : "text-red-600"
                            )}
                          >
                            {template.successRate !== undefined ? `${template.successRate}%` : "—"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-1 pt-1">
                        <button
                          onClick={() => openViewDialog(template)}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1.5"
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/recruitment/interview-templates/${template.id}/edit`)}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1.5"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleClone(template)}
                          disabled={cloneLoadingId === template.id}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 disabled:opacity-50"
                          title="Clone"
                        >
                          {cloneLoadingId === template.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => openSetDefaultDialog(template)}
                          className="text-gray-400 hover:text-amber-500 transition-colors p-1.5"
                          title="Set as default"
                        >
                          <Star size={16} />
                        </button>
                        <button
                          onClick={() => setTemplateToDelete(template)}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1.5"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="mt-0">
          {templates.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 shadow-sm">
              <TrendingUp size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm font-bold text-gray-900">No analytics yet</p>
              <p className="text-xs text-gray-500 font-medium mt-1">Create templates and conduct interviews to see analytics.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Template Overview */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 text-base">
                    Template Overview
                  </h4>
                  <p className="text-gray-500 text-sm font-medium">
                    Questions and duration by template
                  </p>
                </div>

                <div className="space-y-4">
                  {templates.map((t) => {
                    const questionCount = t.questionSets?.reduce((sum, qs) => sum + (qs.questionIds?.length || 0), 0) || 0;
                    const duration = t.questionSets?.reduce((sum, qs) => sum + (qs.durationMinutes || 0), 0) || 0;
                    return (
                      <div key={t.id} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-gray-900">{t.name}</p>
                          <Badge
                            variant="outline"
                            className="rounded-full border-0 font-bold px-2 py-0.5 text-[10px] bg-purple-50 text-purple-700 mt-1"
                          >
                            {TYPE_LABELS[t.type] || t.type}
                          </Badge>
                        </div>
                        <div className="text-right shrink-0 ml-4">
                          <p className="text-sm font-bold text-gray-900">{questionCount} questions</p>
                          <p className="text-xs text-gray-500 font-medium">{duration} min total</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Template Composition */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 text-base">
                    Template Composition
                  </h4>
                  <p className="text-gray-500 text-sm font-medium">
                    Question distribution across templates
                  </p>
                </div>

                <div className="space-y-5">
                  {templates.map((t) => {
                    const questionCount = t.questionSets?.reduce((sum, qs) => sum + (qs.questionIds?.length || 0), 0) || 0;
                    const maxQuestions = Math.max(...templates.map((tm) => tm.questionSets?.reduce((sum, qs) => sum + (qs.questionIds?.length || 0), 0) || 0), 1);
                    return (
                      <div key={t.id} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold text-gray-900">{t.name}</p>
                          <span className="text-sm font-bold text-gray-700">{questionCount} Qs</span>
                        </div>
                        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-slate-900 rounded-full transition-all"
                            style={{ width: `${(questionCount / maxQuestions) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 font-medium">
                          {t.questionSets?.length || 0} question set{(t.questionSets?.length || 0) !== 1 ? "s" : ""}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-0">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            {/* Header */}
            <div>
              <h4 className="font-bold text-gray-900 text-base">
                Template Settings
              </h4>
              <p className="text-gray-500 text-sm font-medium">
                Configure default templates and system settings
              </p>
            </div>

            {/* Default Templates by Department */}
            <div className="space-y-4">
              <h5 className="text-sm font-bold text-gray-900">
                Default Templates by Department
              </h5>
              <div className="space-y-3">
                {[
                  "Executive",
                  "Human Resources",
                  "Finance",
                  "Information Technology",
                  "Operations",
                  "Sales & Marketing",
                ].map((dept) => (
                  <div
                    key={dept}
                    className="flex items-center justify-between gap-4"
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {dept}
                    </span>
                    <select className="bg-white border border-gray-200 rounded-lg h-9 px-3 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100 w-full max-w-[280px]">
                      <option>No default</option>
                      {templates.map((t) => (
                        <option key={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* System Settings */}
            <div className="space-y-4">
              <h5 className="text-sm font-bold text-gray-900">
                System Settings
              </h5>

              <div className="space-y-4">
                {/* Auto-select templates by role */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Auto-select templates by role
                    </p>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                      Automatically suggest templates based on job role
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                {/* Track template effectiveness */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Track template effectiveness
                    </p>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                      Monitor success rates and interview outcomes
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                {/* Require minimum questions */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Require minimum questions
                    </p>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                      Enforce minimum number of questions per template
                    </p>
                  </div>
                  <input
                    type="number"
                    defaultValue={3}
                    className="w-16 h-9 bg-white border border-gray-200 rounded-lg px-3 text-sm font-bold text-gray-900 text-center focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => { if (!open) setTemplateToDelete(null); }}>
        <AlertDialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <AlertDialogHeader className="p-6 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-500" />
              <AlertDialogTitle className="text-lg font-bold text-gray-900">Delete Template</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-gray-500 font-medium pt-1">
              Are you sure you want to delete <span className="font-bold text-gray-700">"{templateToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 pb-6 gap-2">
            <AlertDialogCancel
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              disabled={deleteLoading}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Delete Template"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set as Default Dialog */}
      <Dialog open={defaultDialogOpen} onOpenChange={setDefaultDialogOpen}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">
              Set as Default
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <p className="text-sm text-gray-500 font-medium">
              Select which job roles this template should be the default for.
            </p>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Job Roles</label>
              <MultiSelect
                options={jobRoles.filter((jr) => !jr.archived).map((jr) => ({ value: String(jr.id), label: jr.title }))}
                selected={selectedDefaultJobRoleIds}
                onChange={setSelectedDefaultJobRoleIds}
                placeholder="Select job roles..."
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
                onClick={() => setDefaultDialogOpen(false)}
                disabled={defaultSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
                onClick={handleSetDefault}
                disabled={defaultSubmitting}
              >
                {defaultSubmitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Template Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200 max-h-[80vh]">
          <DialogHeader className="p-6 pb-4 border-b border-gray-100">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {viewingTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5 space-y-5 overflow-y-auto">
            {viewingTemplate && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="rounded-full border-0 font-bold px-2.5 py-1 text-[10px] bg-purple-50 text-purple-700">
                    {TYPE_LABELS[viewingTemplate.type] || viewingTemplate.type}
                  </Badge>
                  <Badge variant="outline" className="rounded-full border-0 font-bold px-2.5 py-1 text-[10px] bg-blue-50 text-blue-700">
                    {LEVEL_LABELS[viewingTemplate.level] || viewingTemplate.level}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      "rounded-full border-0 font-bold px-2.5 py-1 text-[10px]",
                      viewingTemplate.isActive ? "bg-slate-900 text-white" : "bg-gray-100 text-gray-500"
                    )}
                  >
                    {viewingTemplate.isActive ? "Active" : "Inactive"}
                  </Badge>
                  {(viewingTemplate.isDefault || (viewingTemplate.defaultForJobRoleIds && viewingTemplate.defaultForJobRoleIds.length > 0)) && (
                    <Badge variant="outline" className="rounded-full border-0 font-bold px-2.5 py-1 text-[10px] bg-amber-50 text-amber-700">
                      Default
                    </Badge>
                  )}
                </div>

                {viewingTemplate.description && (
                  <p className="text-sm text-gray-600 font-medium">{viewingTemplate.description}</p>
                )}

                {viewingTemplate.defaultForJobRoleIds && viewingTemplate.defaultForJobRoleIds.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Job Roles</p>
                    <div className="flex flex-wrap gap-1.5">
                      {viewingTemplate.defaultForJobRoleIds.map((id) => {
                        const role = jobRoles.find((jr) => String(jr.id) === String(id));
                        return (
                          <Badge key={id} variant="outline" className="rounded-lg border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-600">
                            {role?.title || `Role ${id}`}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {viewingTemplate.departments && viewingTemplate.departments.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Departments</p>
                    <div className="flex flex-wrap gap-1.5">
                      {viewingTemplate.departments.map((d) => (
                        <Badge key={d.id} variant="outline" className="rounded-lg border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-600">
                          {d.departmentName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {viewingTemplate.targetValues && viewingTemplate.targetValues.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Target Values</p>
                    <div className="flex flex-wrap gap-1.5">
                      {viewingTemplate.targetValues.map((tv) => (
                        <Badge key={tv.id} variant="outline" className="rounded-lg border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-600">
                          {tv.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {viewingTemplate.questionSets && viewingTemplate.questionSets.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                      Question Sets ({viewingTemplate.questionSets.length})
                    </p>
                    <div className="space-y-2">
                      {viewingTemplate.questionSets.map((qs, i) => (
                        <div key={i} className="border border-gray-100 rounded-xl p-3 bg-gray-50/30">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-gray-900">{qs.name}</p>
                            <span className="text-xs text-gray-500 font-medium">{qs.durationMinutes}m</span>
                          </div>
                          {qs.description && <p className="text-xs text-gray-500 font-medium mt-0.5">{qs.description}</p>}
                          <p className="text-xs text-gray-400 font-medium mt-1">{qs.questionIds?.length || 0} question{qs.questionIds?.length !== 1 ? "s" : ""}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="px-6 pb-6 pt-2 border-t border-gray-100 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => setViewDialogOpen(false)}
            >
              Close
            </Button>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => {
                setViewDialogOpen(false);
                if (viewingTemplate) navigate(`/recruitment/interview-templates/${viewingTemplate.id}/edit`);
              }}
            >
              <Pencil size={14} className="mr-1.5" />
              Edit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InterviewTemplates;
